import { closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import debounce from "awesome-debounce-promise";
import { clsx } from "clsx";
import { produce } from "immer";
import { clone, filter, pipe } from "ramda";
import { type FC, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import WindowedSelect from "react-windowed-select";
import {
  Badge,
  Callout,
  CalloutIntent,
  HStack,
  Spacer,
  TriggerButton,
  TriggerButtonIntent,
  TriggerButtonSize,
} from "widgets";
import { moveItemsInArray } from "widgets/utils";

import { createDiscreteColorGradient } from "@/app-utils/color.ts";
import type { ISelectOption } from "@/app-utils/form.ts";
import type { ColumnAnnotation, DataSeries, Dataset } from "@/graphql.ts";
import {
  getCommonSplitByCondition,
  getParentDataSeries,
  type IColorSettings,
  recalculateDataSeriesColors,
  type SelectedDataSeries,
  shouldShowDataSeriesInLegend,
  toggleDisplayOfDataSeriesWithoutData,
} from "@/redux/modules/dataset-viewer.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DataSeriesPropertiesDialog } from "@/ui-modules/analytical-viewer/DataSeriesPropertiesDialog.tsx";

import { SortableList } from "../core/forms/Sortable.js";
import { ChartSettingsWizardStep } from "./ChartSettings.js";
import { DataSeriesItem, DataSeriesSplitItem, type IDataSeriesItemProps } from "./DataSeriesItem.js";

export interface IDataSeriesSelectorProps {
  label: string; // the title text at the top (e.g. "Data Series")
  dataset: Dataset;
  wizardStep: ChartSettingsWizardStep;
  isLoadingDataSeries: boolean;
  dataSeriesNames: string[]; // list of series names, result of a search or the first N series of the data set
  selectedDataSeries: SelectedDataSeries[]; // name, color and split condition of selected data series, see SEL5-663
  colorSettings: IColorSettings; // color set and color to data set assignment
  availableSplitByConditions: ColumnAnnotation[]; // aka, availableAnnotations
  selectedConditions: ColumnAnnotation[]; // annotation list is required to trigger the update of the rerender of a tooltip
  isSampleMode: boolean; // either sample or dataset mode

  handleSelectedDataSeriesChanged: (selectedDataSeries: SelectedDataSeries[]) => void; // the callback reporting changes to the data series back to the viewer
  handleColorSettingsChanged: (colorSettings: IColorSettings) => void;
  handleSplitByCondition: (dataSeries: SelectedDataSeries, splitCondition: ColumnAnnotation | null) => void;

  onSearch: (searchTerm: string) => Promise<DataSeries[]>; // callback invoked when the user types in the react-select field
  onAdd: (name: string) => void; // callback adding a data series line to selectedDataSeries
}

const DEBOUNCE_TIMER = 300;

const DataSeriesSelector: FC<IDataSeriesSelectorProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const hideEmptyDataSeries = useSelector<IAppState, boolean>(
    (state) => state.datasetViewer.hideEmptyDataSeries,
    shallowEqual,
  );
  // SEL6-2681: for debouncing Promises, use awesome-debounce-promise library, together with onlyResolvesLast option to support the react-select's cache feature
  const loadOptions = debounce((searchTerm: string) => props.onSearch(searchTerm), DEBOUNCE_TIMER, {
    onlyResolvesLast: true,
  });

  // The list of data series without the already selected series converted to the options structure.
  const dataSeriesOptions = useMemo<ISelectOption[]>(
    () =>
      props.dataSeriesNames
        .filter((d: string) => !props.selectedDataSeries.find((s) => s.name === d))
        .map((d: string) => ({ label: d, value: d })),
    [props.dataSeriesNames, props.selectedDataSeries],
  );

  const dataSeriesItemProps = useMemo<IDataSeriesItemProps>(
    () => ({
      splitConditions: props.availableSplitByConditions,
      handleRemoveItem: (removedItem: SelectedDataSeries) => {
        const f = pipe(
          filter((item: SelectedDataSeries) => item.name !== removedItem.name),
          filter((item: SelectedDataSeries) => item.splitParent !== removedItem.name), // also remove the data series generated from splitByCondition
        );

        const updatedSelectedDataSeries: SelectedDataSeries[] = f(props.selectedDataSeries);

        props.handleSelectedDataSeriesChanged(updatedSelectedDataSeries);

        if (
          removedItem.splitParent && // if this is the last child of a parent split by condition then we reset the split by condition at all from this parent
          !updatedSelectedDataSeries.find(
            (dataSeries: SelectedDataSeries) => dataSeries.splitParent === removedItem.splitParent,
          )
        ) {
          const found = updatedSelectedDataSeries.find((ds: SelectedDataSeries) => ds.name === removedItem.splitParent);

          if (found) props.handleSplitByCondition(found, null);
        }
      },
      handleChangeColor: (item: SelectedDataSeries, color: string) => {
        const updatedDataSeries: SelectedDataSeries[] = clone(props.selectedDataSeries).map(
          (ds: SelectedDataSeries) => {
            if (ds.name === item.name) ds.color = color;
            return ds;
          },
        );

        // if this is split by annotation then we also change the colors of the child data series
        if (item.splitByCondition) {
          // get the number of split values
          const splitItems: SelectedDataSeries[] = updatedDataSeries.filter(
            (ds: SelectedDataSeries) => ds.splitParent === item.name,
          );
          const colorGradients: string[] = createDiscreteColorGradient(color, splitItems.length);
          splitItems.forEach((ds: SelectedDataSeries, index: number) => (ds.color = colorGradients[index]));
        }

        props.handleSelectedDataSeriesChanged(updatedDataSeries);
        props.handleColorSettingsChanged(
          produce(props.colorSettings, (draft) => {
            draft.rowColors![item.name!] = color;
          }),
        );
      },
      handleChangeHidden: (dataSeries: SelectedDataSeries) => {
        const pos = props.selectedDataSeries.findIndex((d) => d.name === dataSeries.name);
        const newDataSeries: SelectedDataSeries[] = produce(props.selectedDataSeries, (draft) => {
          draft[pos].hidden = !dataSeries.hidden;
        });
        props.handleSelectedDataSeriesChanged(newDataSeries);
      },
      handleSplitConditionSelected: (splitCondition: ColumnAnnotation, dataSeries: SelectedDataSeries) => {
        props.handleSplitByCondition(dataSeries, splitCondition);
      },
      dataSeries: null,
    }),
    [props],
  );

  useEffect(() => {
    props.onSearch("");
  }, []);

  const [isColorpickerOpen, setColorPickerOpen] = useState<boolean>(false);
  const [activeDraggedItem, setActiveDraggedItem] = useState<number | undefined>();

  const sortableItems = useMemo(
    () => props.selectedDataSeries.map((dataserie) => dataserie.name).filter((name): name is string => name !== null),
    [props.selectedDataSeries],
  );

  const parentSelectedDataSeries = getParentDataSeries(props.selectedDataSeries);
  const commonSplitByCondition = getCommonSplitByCondition(parentSelectedDataSeries);
  const numberOfParentsWithSplitCondition = parentSelectedDataSeries.filter((p) => p.splitByCondition).length;

  return (
    <div className="chart-data-series-selector selector-component noselect">
      <div className="form-group">
        <span id="data-viewer-data-series-selection" className="hotspot-beacon" />
        <HStack>
          <label className={clsx({ ["no-margin"]: parentSelectedDataSeries.length > 1 })}>{props.label}</label>
          {parentSelectedDataSeries.length > 1 && (
            <DataSeriesPropertiesDialog
              dataSeries={undefined}
              splitConditions={props.availableSplitByConditions}
              defaultSplitCondition={commonSplitByCondition as string}
              handleChangeHidden={() => {}}
              handleSplitConditionSelected={(condition, series) => props.handleSplitByCondition(series, condition)}
            />
          )}
          {numberOfParentsWithSplitCondition > 0 && (
            <>
              <TriggerButton
                onPress={() => dispatch(recalculateDataSeriesColors())}
                size={TriggerButtonSize.Small}
                icon={"colors"}
                label={"Re-calculate split data series colors"}
              />
              <Spacer />
              <TriggerButton
                onPress={() => dispatch(toggleDisplayOfDataSeriesWithoutData())}
                size={TriggerButtonSize.Small}
                intent={hideEmptyDataSeries ? TriggerButtonIntent.Active : undefined}
                icon={"compress"}
                label={
                  hideEmptyDataSeries
                    ? "Show Split Data Series Without Values"
                    : "Hide Split Data Series Without Values"
                }
              />
            </>
          )}
        </HStack>
        <SortableList
          collisionDetection={closestCenter}
          onDragStart={({ active }) => {
            if (active) {
              setActiveDraggedItem(active.data.current?.sortable.index);
            } else {
              setActiveDraggedItem(undefined);
            }
          }}
          onDragCancel={() => {
            setActiveDraggedItem(undefined);
          }}
          onDragEnd={({ active, over }) => {
            setActiveDraggedItem(undefined);
            props.handleSelectedDataSeriesChanged(
              moveItemsInArray<SelectedDataSeries, any, any>(props.selectedDataSeries, active, over, "name"),
            );
          }}
        >
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy} // allows a better handling of items of different sizes e.g. split data series
            disabled={isColorpickerOpen}
          >
            {props.selectedDataSeries.map((dataserie: SelectedDataSeries, index: number) => {
              const currentDataSerie = props.selectedDataSeries.filter(
                (ds: SelectedDataSeries) => ds.splitParent === dataserie.name,
              );
              return dataserie.splitParent ? undefined : (
                <DataSeriesItem
                  {...dataSeriesItemProps}
                  dataSeries={dataserie}
                  key={`dataseries-item-${dataserie.name}`}
                  isOpen={(isOpen) => setColorPickerOpen(isOpen)}
                  isActive={activeDraggedItem !== undefined && activeDraggedItem === index}
                >
                  {activeDraggedItem !== undefined && currentDataSerie.length > 0 && activeDraggedItem === index ? (
                    <Badge
                      label={currentDataSerie.length + 1}
                      style={{ position: "absolute", top: 0, left: 0 }}
                      intent="primary"
                    />
                  ) : (
                    currentDataSerie
                      .filter((splitSeries) => {
                        return dispatch(shouldShowDataSeriesInLegend(splitSeries));
                      })
                      .map((splitSeries: SelectedDataSeries, splitIndex: number) => (
                        <DataSeriesSplitItem
                          key={`item-${index}-${splitIndex}`}
                          {...dataSeriesItemProps}
                          dataSeries={splitSeries}
                          isOpen={(isOpen) => setColorPickerOpen(isOpen)}
                        />
                      ))
                  )}
                </DataSeriesItem>
              );
            })}
          </SortableContext>
        </SortableList>

        {dataSeriesOptions ? (
          <>
            {!!(dataSeriesOptions.length > 0 && props.wizardStep === ChartSettingsWizardStep.dataSeriesMissing) && (
              <Callout intent={CalloutIntent.Info} style={{ marginBottom: 5 }}>
                Select {props.isSampleMode ? "y-axis" : "a data series"}
              </Callout>
            )}
            <div style={{ marginTop: 5 }}>
              <WindowedSelect
                className="react-select dataseries-selector"
                classNamePrefix="react-select"
                name="dataSeriesSelection"
                key="dataSeriesSelector"
                getOptionValue={(option: ISelectOption) => option.value}
                noOptionsMessage={() => "No matching data series..."}
                isDisabled={!props.dataset}
                placeholder={dataSeriesOptions.length === 0 ? "No data available for display" : "Add data series..."}
                isClearable={false}
                isSearchable={true}
                options={dataSeriesOptions}
                formatOptionLabel={(option: ISelectOption) => <div className="dataseries-option">{option.label}</div>}
                isMulti={false}
                onBlurResetsInput={true}
                onChange={(option: ISelectOption) => props.onAdd(option.value)}
                controlShouldRenderValue={false}
                onInputChange={(term) => {
                  loadOptions(term);
                }}
                isLoading={props.isLoadingDataSeries}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export { DataSeriesSelector };
